import { Col, Container } from 'react-bootstrap';
import { useParams, useLocation } from 'react-router';
import { Outlet } from 'react-router-dom';
import jwtDecode from 'jwt-decode';
import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useMemo } from 'react';
import NestedConvert from '../shared/components/Navigation/NestedConvert';
import {
  useGetAccountDataQuery,
  useGetConfigurationQuery,
} from '../shared/navigation/apiSlice';
import { NavItem } from '../shared/navigation/navigationRouting';
import HubAccountSelect from './HubAccountSelect';
import { hubLayoutSelector } from './hubLayoutSlice';
import { useGetProductsQuery } from './TrainingHub/apiSlice';
import { userSelector } from '../shared/store/shared/userSlice';
import { JwtDecodedUser } from '../shared/models/UserModels';
import Footer from '../shared/components/Footer';
import MobileNav from '../shared/components/Navigation/MobileNav';
import SideNav from '../shared/components/Navigation/SideNav';
import Cart from '../shared/components/Cart';
import {
  cartSelector,
  closeCart,
  updateBadgeCount,
} from '../shared/store/shared/cartSlice';
import { useGetCartDetailsQuery } from '../shared/components/Cart/apiSlice';

export default function Hub() {
  const { accountId } = useParams<{ accountId: string }>();
  const location = useLocation();
  const isConfirmationPage = location.pathname.includes('/confirmation');
  const { value: user } = useSelector(userSelector);
  const accessToken = user ? user.access_token : '';
  const authUser: JwtDecodedUser = accessToken ? jwtDecode(accessToken) : null;
  const dispatch = useDispatch();
  const { isOpen } = useSelector(cartSelector);
  const hasValidCredentials =
    accountId &&
    authUser?.b2b_aid &&
    authUser?.b2b_auid &&
    authUser?.b2b_aid.toLocaleUpperCase() === accountId.toLocaleUpperCase();

  const {
    data: hubRoutes,
    isLoading,
    isFetching,
  } = useGetConfigurationQuery(
    {
      type: 'hub',
      accountId: '',
    },
    {
      skip: !hasValidCredentials,
    },
  );

  const {
    data: hub,
    isLoading: isProductsLoading,
    isError: isProductsError,
    isFetching: isProductsFetching,
  } = useGetProductsQuery(
    { accountId },
    {
      skip: !hasValidCredentials,
    },
  );

  const {
    data: cartProducts,
    isLoading: isCartProductsLoading,
    isFetching: isCartProductsFetching,
  } = useGetCartDetailsQuery(
    {
      accountId,
    },
    { skip: !accountId || !hub?.showPayByUserCart },
  );

  const { data: accountData, isLoading: accountDataLoading } =
    useGetAccountDataQuery(accountId, {
      skip: !hasValidCredentials,
    });

  const { pageTitle } = useSelector(hubLayoutSelector);

  const newHubRoute = useMemo(() => {
    if (isProductsFetching || isProductsError || !hub || accountDataLoading)
      return [];

    const routes = {
      ...hubRoutes,
      menus: [
        {
          id: hubRoutes?.menus[0]?.id,
          title: hubRoutes?.menus[0]?.title,
          children: hubRoutes?.menus[0]?.children?.map((menu: NavItem) => {
            const clone = { ...menu };
            clone.href = clone.href.replace('{accountId}', accountId);
            return clone;
          }),
        },
      ],
    };

    return NestedConvert([...routes.menus]);
  }, [
    isFetching,
    isLoading,
    isProductsFetching,
    isProductsLoading,
    isProductsError,
  ]);

  useEffect(() => {
    if (pageTitle) {
      if (accountData && accountData?.name) {
        document.title = `${pageTitle} - ${accountData?.name} | PMI Partner Platform`;
      } else {
        document.title = `${pageTitle} | PMI Partner Platform`;
      }
    } else {
      document.title = 'PMI Partner Platform';
    }
  }, [accountData, pageTitle]);

  useEffect(() => {
    if (cartProducts?.items) {
      dispatch(updateBadgeCount(cartProducts.items.length));
    }
  }, [cartProducts]);

  const navigationLinks = useMemo(
    () =>
      newHubRoute?.length > 0
        ? newHubRoute[0]?.children?.map((i: NavItem) => ({
            ...i,
            title: i.title.includes('Platform') ? 'Products' : i.title,
            link: {
              href: i.href,
              text: i.title.includes('Platform') ? 'Products' : i.title,
            },
          }))
        : [],
    [newHubRoute],
  );

  return (
    <>
      {/* Cart is rendered here with props */}
      {hub?.showPayByUserCart && (
        <Cart
          showPhoneNumber={hub?.showPayByUserPhoneNumber}
          userProducts={hub?.userProducts || []}
          cartDetails={cartProducts}
          accountName={accountData?.name}
          isOpen={isOpen} // Controls slide-in animation
          onClose={() => dispatch(closeCart())} // Passed to close on outside click or close button
          productsLoading={
            isCartProductsLoading ||
            isCartProductsFetching ||
            isProductsLoading ||
            isProductsFetching
          }
          isCartProductsLoading={
            isCartProductsFetching || isCartProductsLoading
          }
        />
      )}

      <MobileNav items={navigationLinks} title="Partner Platform" />
      <Container fluid className="d-flex flex-column min-vh-100 p-0">
        <div className="d-flex flex-grow-1">
          {accountId && (
            <Col xs={0} md={3} lg={2} className="px-0">
              <SideNav title="Partner Platform" items={navigationLinks} />
            </Col>
          )}
          <Col
            xs={12}
            md={accountId ? 9 : 12}
            lg={10}
            className={`${
              isConfirmationPage ? 'p-2' : 'p-5'
            } mx-auto max-xl-container`}
          >
            {!isConfirmationPage && (
              <>
                {accountData && <p>{accountData.name} Training</p>}
                <h1 className="pb-3 mb-4 border-bottom fw-bold fs-2 lh-sm">
                  {pageTitle}
                </h1>
              </>
            )}
            {!accountId ? <HubAccountSelect /> : <Outlet />}
          </Col>
        </div>
        <Footer />
      </Container>
    </>
  );
}
